/**
 * Încarcă modelul unei mașini: alege varianta (mobil/desktop) și LOD-ul, decomprimă meshopt și KTX2,
 * revalidează dimensiunile, construiește rig-ul și eliberează memoria la descărcare.
 */
import type { Object3D } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import type { Issue, VehicleDefinition } from '../data/types';
import { checkModel } from './modelCheck';
import { buildRig, type CarRig } from './rig';
import type { Variant } from './variant';

export interface Manifest {
  id: string;
  variants: Partial<Record<Variant, Record<string, { file: string; bytes: number; triangles: number; gpuBytes: number }>>>;
}

export interface LoadedCar {
  root: Object3D;
  rig: CarRig;
  issues: Issue[];
  variant: Variant;
  lod: string;
  bytes: number;
  dispose(): number;
}

/** Sursa fișierelor: în joc, din /cars/; în pagina de pe claude.ai, din date incluse. */
export interface ModelSource {
  manifest(id: string): Promise<Manifest>;
  file(id: string, name: string): Promise<ArrayBuffer>;
}

export const httpSource = (base = '/cars'): ModelSource => ({
  manifest: async (id) => (await fetch(`${base}/${id}/manifest.json`)).json(),
  file: async (id, name) => {
    const r = await fetch(`${base}/${id}/${name}`);
    if (!r.ok) throw new Error(`${name}: HTTP ${r.status}`);
    return r.arrayBuffer();
  }
});

let ktx2: KTX2Loader | null = null;

export function createLoader(renderer?: unknown, transcoderPath = '/basis/'): GLTFLoader {
  const loader = new GLTFLoader();
  loader.setMeshoptDecoder(MeshoptDecoder);
  if (renderer && transcoderPath) {
    try {
      ktx2 ??= new KTX2Loader().setTranscoderPath(transcoderPath).detectSupport(renderer as never);
      loader.setKTX2Loader(ktx2);
    } catch {
      /* fără KTX2: modelele fără texturi KTX2 se încarcă normal */
    }
  }
  return loader;
}

export async function loadCar(v: VehicleDefinition, variant: Variant, lod: string, source: ModelSource, loader: GLTFLoader): Promise<LoadedCar> {
  const manifest = await source.manifest(v.id);
  const pick = manifest.variants[variant] ?? manifest.variants[variant === 'mobile' ? 'desktop' : 'mobile'];
  if (!pick) throw new Error(`modelul ${v.id} nu are variante`);
  const entry = pick[lod] ?? pick.lod0;
  const buf = await source.file(v.id, entry.file);
  const gltf = await loader.parseAsync(buf, '');
  const root = gltf.scene;
  root.name = `car:${v.id}`;
  const issues = checkModel(root, v);
  const rig = buildRig(root, v);
  return { root, rig, issues, variant: (manifest.variants[variant] ? variant : variant === 'mobile' ? 'desktop' : 'mobile'), lod: entry === pick[lod] ? lod : 'lod0', bytes: entry.bytes, dispose: () => rig.dispose() };
}
